///
/// EarthBrowser
///
/// Created by Matt Giger on September 19, 2026
/// Copyright (c) 2026 Geodata Labs
///
/// Email deliverability API: send-counter stats and the suppression list.
///

import { authenticatedApi } from './authenticatedApi'

export interface DeliverabilityWindow {
  days: number
  sent: number
  blocked: number
  failed: number
  newBounces: number
  newComplaints: number
  newUnsubscribes: number
  bounceRate: number | null
  complaintRate: number | null
}

export interface DailySendCounts {
  day: string
  sent: number
  blocked: number
  failed: number
}

export interface EmailSuppression {
  email: string
  reason: 'unsubscribe' | 'bounce' | 'complaint'
  added_at: string
  expires_at: string | null
  details: Record<string, unknown> | null
}

export const emailApi = authenticatedApi.injectEndpoints({
  endpoints: (builder) => ({
    getEmailDeliverability: builder.query<{
      window: DeliverabilityWindow
      daily: DailySendCounts[]
      suppressionTotals: Record<string, number>
    }, void>({
      query: () => '/api/admin/email/deliverability',
      providesTags: ['EmailDeliverability'],
    }),

    getEmailSuppressions: builder.query<{
      suppressions: EmailSuppression[]
      pagination: { page: number; limit: number; total: number; pages: number }
    }, {
      search?: string
      reason?: string
      page?: number
    }>({
      query: ({ search, reason, page = 1 }) => {
        const params = new URLSearchParams()
        params.set('page', String(page))
        if (search) params.set('search', search)
        if (reason) params.set('reason', reason)
        return `/api/admin/email/suppressions?${params}`
      },
      providesTags: ['EmailSuppressions'],
    }),

    removeEmailSuppression: builder.mutation<{ success: boolean }, { email: string; note?: string }>({
      query: ({ email, note }) => ({
        url: `/api/admin/email/suppressions/${encodeURIComponent(email)}`,
        method: 'DELETE',
        body: { note },
      }),
      invalidatesTags: ['EmailSuppressions', 'EmailDeliverability'],
    }),
  }),
})

export const {
  useGetEmailDeliverabilityQuery,
  useGetEmailSuppressionsQuery,
  useRemoveEmailSuppressionMutation,
} = emailApi
